import { useRender } from '@base-ui/react/use-render'
import { createContext, use, useEffect, useId, useMemo, useState } from 'react'

import { cn } from '../../../lib/utils.ts'
import { Heading } from '../heading/heading.tsx'
import { Skeleton } from '../skeleton/skeleton.tsx'
import { Text } from '../text/text.tsx'

import {
  sectionBodyVariants,
  sectionHeaderVariants,
  sectionRootVariants,
  sectionTitleVariants,
  type SectionLevel,
} from './section.variants.ts'

type SectionContextValue = {
  level: SectionLevel
  registerTitle: (titleId: string | undefined) => void
  registerSubtitle: (subtitleId: string | undefined) => void
}

const SectionContext = createContext<SectionContextValue | null>(null)

function useSectionContext(): SectionContextValue {
  const context = use(SectionContext)
  if (context === null) throw new Error('Section part rendered outside Section.Root')
  return context
}

function getLabelledBy(
  level: SectionLevel,
  titleId: string | undefined,
  subtitleId: string | undefined,
): string | undefined {
  if (titleId === undefined) return undefined
  if (level === 1 || subtitleId === undefined) return titleId
  return `${titleId} ${subtitleId}`
}

/**
 * `Section` is a titled content block: `Section.Root` + `Section.Header`
 * (`Section.Title` + optional `Section.Subtitle` and `Section.Actions`) + `Section.Body`. A layout
 * primitive like `Page`/`Item` with level-aware slots.
 *
 * `Section.Title` registers its id so `Section.Root` exposes the `<section>` as
 * an `aria-labelledby` landmark named by its title, or by its title and subtitle at any level
 * except 1 (and stays unlabelled when there is no title).
 * `Header`, `Title`, `Subtitle` and `Body` require a `Section.Root` ancestor.
 *
 * @summary Composable titled section (header / title / actions / body).
 */
function SectionRoot(props: SectionRoot.Props) {
  const { className, level = 1, ...rest } = props
  const [titleId, setTitleId] = useState<string>()
  const [subtitleId, setSubtitleId] = useState<string>()
  const contextValue = useMemo<SectionContextValue>(
    () => ({ level, registerTitle: setTitleId, registerSubtitle: setSubtitleId }),
    [level],
  )

  return (
    <SectionContext value={contextValue}>
      <section
        data-slot="section-root"
        aria-labelledby={getLabelledBy(level, titleId, subtitleId)}
        className={cn(sectionRootVariants({ level }), className)}
        {...rest}
      />
    </SectionContext>
  )
}
namespace SectionRoot {
  export type Props = React.ComponentProps<'section'> & { level?: SectionLevel }
}

function SectionHeader(props: SectionHeader.Props) {
  const { className, ...rest } = props
  const { level } = useSectionContext()
  return (
    <div
      data-slot="section-header"
      // Two columns so the title and its subtitle stack on the left while actions stay right.
      // `row-span-full` on the actions spans only the rows that exist, so a header without a
      // subtitle renders exactly as it did when this was a flex row.
      className={cn(sectionHeaderVariants({ level }), className)}
      {...rest}
    />
  )
}
namespace SectionHeader {
  export type Props = React.ComponentProps<'div'>
}

function SectionTitleRoot(props: SectionTitleRoot.Props) {
  const { className, children, id, ...rest } = props
  const generatedId = useId()
  const titleId = id ?? generatedId
  const sectionContext = useSectionContext()
  const { level } = sectionContext

  useEffect(
    function registerTitleId() {
      sectionContext.registerTitle(titleId)
      return function unregisterTitleId() {
        sectionContext.registerTitle(undefined)
      }
    },
    [sectionContext, titleId],
  )

  switch (level) {
    case 1:
      return (
        <Heading
          data-slot="section-title"
          id={titleId}
          variant="subtitle"
          level={2}
          className={cn(sectionTitleVariants({ level }), className)}
          {...rest}
        >
          {children}
        </Heading>
      )
    case 2:
      return (
        <Text
          data-slot="section-title"
          id={titleId}
          variant="body2"
          weight="regular"
          render={<h3 />}
          className={cn(sectionTitleVariants({ level }), className)}
          {...rest}
        >
          {children}
        </Text>
      )
    case 3:
      return (
        <Text
          data-slot="section-title"
          id={titleId}
          variant="caption"
          weight="semibold"
          render={<h4 />}
          className={cn(sectionTitleVariants({ level }), className)}
          {...rest}
        >
          {children}
        </Text>
      )
  }
}
namespace SectionTitleRoot {
  export type Props = useRender.ComponentProps<'h2'>
}

function SectionTitleSkeleton(props: SectionTitleSkeleton.Props) {
  const { className, height, ...rest } = props
  return (
    <Skeleton
      {...rest}
      data-slot="section-title-skeleton"
      variant="text"
      height={height ?? 'var(--leading-50)'}
      className={cn('col-start-1', className)}
    />
  )
}
namespace SectionTitleSkeleton {
  export type Props = Omit<Skeleton.Props, 'variant'>
}

const SectionTitle = Object.assign(SectionTitleRoot, { Skeleton: SectionTitleSkeleton })

function SectionSubtitleRoot(props: SectionSubtitleRoot.Props) {
  const { className, id, ...rest } = props
  const generatedId = useId()
  const subtitleId = id ?? generatedId
  const sectionContext = useSectionContext()

  useEffect(
    function registerSubtitleId() {
      sectionContext.registerSubtitle(subtitleId)
      return function unregisterSubtitleId() {
        sectionContext.registerSubtitle(undefined)
      }
    },
    [sectionContext, subtitleId],
  )

  return (
    <Text
      id={subtitleId}
      variant="body2"
      render={<div />}
      data-slot="section-subtitle"
      className={cn('col-start-1 text-content-base-basic', className)}
      {...rest}
    />
  )
}
namespace SectionSubtitleRoot {
  export type Props = React.ComponentProps<'div'>
}

function SectionSubtitleSkeleton(props: SectionSubtitleSkeleton.Props) {
  const { className, height, ...rest } = props
  return (
    <Skeleton
      {...rest}
      data-slot="section-subtitle-skeleton"
      variant="text"
      height={height ?? 'var(--leading-50)'}
      className={cn('col-start-1', className)}
    />
  )
}
namespace SectionSubtitleSkeleton {
  export type Props = Omit<Skeleton.Props, 'variant'>
}

const SectionSubtitle = Object.assign(SectionSubtitleRoot, { Skeleton: SectionSubtitleSkeleton })

function SectionActions(props: SectionActions.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="section-actions"
      className={cn(
        `
          col-start-2 row-span-full row-start-1 flex shrink-0 items-center gap-inline
          group-has-[>[data-slot=section-subtitle]]/section-header:row-[1/span_2]
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace SectionActions {
  export type Props = React.ComponentProps<'div'>
}

function SectionBody(props: SectionBody.Props) {
  const { className, ...rest } = props
  const { level } = useSectionContext()
  return <div data-slot="section-body" className={cn(sectionBodyVariants({ level }), className)} {...rest} />
}
namespace SectionBody {
  export type Props = React.ComponentProps<'div'>
}

const Section = {
  Root: SectionRoot,
  Header: SectionHeader,
  Title: SectionTitle,
  Subtitle: SectionSubtitle,
  Actions: SectionActions,
  Body: SectionBody,
}

namespace Section {
  export namespace Root {
    export type Props = SectionRoot.Props
  }
  export namespace Header {
    export type Props = SectionHeader.Props
  }
  export namespace Title {
    export type Props = SectionTitleRoot.Props
    export namespace Skeleton {
      export type Props = SectionTitleSkeleton.Props
    }
  }
  export namespace Subtitle {
    export type Props = SectionSubtitleRoot.Props
    export namespace Skeleton {
      export type Props = SectionSubtitleSkeleton.Props
    }
  }
  export namespace Actions {
    export type Props = SectionActions.Props
  }
  export namespace Body {
    export type Props = SectionBody.Props
  }
}

export { Section }
